import { BusinessCountry } from '@query'

export enum AppMarket {
  FI = 'fi',
  DE = 'de'
}

const isDevboxHost = (): boolean =>
  typeof window !== 'undefined' && window.location.hostname.endsWith('.devbox.nocfo.io')

/** Vite `DEV` is false for `vite preview` on a feature box; keep the same local-storage switch. */
export const allowsAppMarketOverride = (): boolean => Boolean(import.meta.env.DEV) || isDevboxHost()

const APP_MARKET_OVERRIDE_KEY = 'nocfo-app-market-override'

export const parseAppMarket = (value: string | null): AppMarket | null => {
  if (!value) {
    return null
  }
  const normalized = value.trim().toLowerCase()
  return Object.values(AppMarket).find(market => market === normalized) ?? null
}

const marketFromEnv = (): AppMarket =>
  parseAppMarket(import.meta.env.VITE_APP_MARKET) ?? AppMarket.FI

export const getAppMarket = (): AppMarket => {
  if (allowsAppMarketOverride() && typeof window !== 'undefined') {
    const override = parseAppMarket(window.localStorage.getItem(APP_MARKET_OVERRIDE_KEY))
    if (override) {
      return override
    }
  }
  return marketFromEnv()
}

export const setAppMarketOverride = (market: AppMarket): void => {
  window.localStorage.setItem(APP_MARKET_OVERRIDE_KEY, market)
  window.location.reload()
}

export const switchAppMarket = (market: AppMarket): void => {
  if (market === getAppMarket()) return

  if (allowsAppMarketOverride()) {
    setAppMarketOverride(market)
    return
  }

  const host = APP_HOSTS[market]
  if (!host) return
  const next = new URL(window.location.href)
  next.hostname = host
  window.location.assign(next.toString())
}

export const APP_MARKET: AppMarket = getAppMarket()

const MARKET_HEADERS = {
  [AppMarket.FI]: 'FI',
  [AppMarket.DE]: 'DE'
} as const

export const toMarketHeader = (market: AppMarket) => MARKET_HEADERS[market]

const APP_HOSTS: Record<AppMarket, string> = {
  [AppMarket.FI]: import.meta.env.VITE_APP_HOST_FI,
  [AppMarket.DE]: import.meta.env.VITE_APP_HOST_DE
}

const ALL_APP_HOSTS = Object.values(APP_HOSTS).filter(Boolean)

const marketFromCountry = (country: BusinessCountry | string): AppMarket =>
  parseAppMarket(String(country)) ?? AppMarket.FI

export const canonicalAppHost = (
  country: BusinessCountry | string,
  hostname: string
): string | null => {
  const wanted = APP_HOSTS[marketFromCountry(country)]
  if (!wanted || !ALL_APP_HOSTS.includes(hostname)) {
    return null
  }
  return hostname === wanted ? null : wanted
}

export const redirectToCanonicalAppHost = (country: BusinessCountry | string): void => {
  const host = canonicalAppHost(country, window.location.hostname)
  if (!host) {
    return
  }
  const next = new URL(window.location.href)
  next.hostname = host
  window.location.replace(next.toString())
}
